import { useQuery } from "@tanstack/react-query";
import {
  getUnlockedProductListingIds,
  membershipsWithUnlockedQuery,
  unlockedListingsQuery,
} from "modules/shopVisibility";
import cx from "classnames";
import { useEffect, useRef } from "react";
import { matchPath, NavLink, Outlet, useLocation, useMatch, useNavigation } from "react-router-dom";

import {
  getGiftCards,
  getPlatform,
  getRetailproductsByPlatformIdProductListings,
  getShop,
  getShopCustomCategoriesListings,
} from "@/api/methods";
import { useTranslation } from "@/core/i18n";
import { ALGOLIA_PRODUCT_TYPES } from "@/utils/algoliaCatalog";
import type { Models } from "api/types";
import { useCatalogListingsVerification, useCatalogSummary } from "hooks/useCatalogSummary";
import { useMyProductsVisibility } from "hooks/useMyProductsVisibility";
import { useRecommendationsEligibility } from "hooks/useRecommendationsEligibility";
import { useSkinDropVisibility } from "hooks/useSkinDropVisibility";
import { useAdonisChat } from "modules/adonis/AdonisChatProvider/AdonisChatProvider";
import AdonisAiIcon from "modules/adonis/assets/ai-icon.svg";
import { publicStorefrontPaths } from "modules/storefront/publicStorefrontRoute";
import { useUser, useUserPreferences } from "modules/user/useUser/useUser";
import TopNav from "molecules/navbars/TopNav";
import { getCustomCategoryDestinationUrl } from "toolbox/CustomCategories";
import {
  ShopGiftCardsSkeleton,
  ShopListingSkeleton,
  ShopMembershipsSkeleton,
  ShopProductsSkeleton,
} from "./shop/ShopListingSkeleton";
import { PendingRouteViewport } from "./PendingRouteViewport";

type NavButtonProps = {
  exact?: boolean;
  to: string;
  children: React.ReactNode;
};
export const NavButton = ({ exact, to, children }: NavButtonProps) => {
  const isActive = !!useMatch(to);
  const navigation = useNavigation();
  const shopNavigationPending =
    navigation.state === "loading" && navigation.location?.pathname.startsWith("/shop/");
  const itemRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (itemRef.current && isActive && !shopNavigationPending) {
      itemRef.current?.scrollIntoView({
        behavior: "auto",
        inline: "nearest",
        block: "nearest",
      });
    }
  }, [isActive, shopNavigationPending]);

  return (
    <NavLink
      to={to}
      end={exact}
      className={(p) =>
        cx(
          "flex min-h-11 shrink-0 scroll-ml-6 items-center border-b-2 py-2 text-bold2 hover:text-brand-color focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-color active:opacity-60 motion-safe:transition-colors",
          p.isPending || (p.isActive && !shopNavigationPending)
            ? "border-brand-color text-brand-color"
            : "border-transparent",
          p.isPending && "opacity-60",
        )
      }
      ref={itemRef}
    >
      {children}
    </NavLink>
  );
};

/** Whether an unlocked unlisted listing of `type` should keep its shop tab visible. */
export const hasUnlockedListingOfType = (
  unlockedListings: readonly Pick<Models<"ListingModel">, "type">[] | undefined,
  type: Models<"ListingModel">["type"],
): boolean => unlockedListings?.some((listing) => listing.type === type) ?? false;

type ListingTabVisibility = {
  catalogCount: number | undefined;
  isCatalogPending: boolean;
  verifiedListings: readonly Pick<Models<"ListingModel">, "type">[] | undefined;
  unlockedListings: readonly Pick<Models<"ListingModel">, "type">[] | undefined;
  type: Models<"ListingModel">["type"];
};

/**
 * A zero from the search index is verified against the location-scoped catalog API before it can
 * expose navigation. Initial summary loading preserves the existing stable nav; a settled zero
 * stays hidden until REST confirms the destination has content.
 */
export const shouldShowListingTab = ({
  catalogCount,
  isCatalogPending,
  verifiedListings,
  unlockedListings,
  type,
}: ListingTabVisibility): boolean => {
  if (typeof catalogCount === "number" && catalogCount > 0) return true;
  if (hasUnlockedListingOfType(unlockedListings, type)) return true;
  if (hasUnlockedListingOfType(verifiedListings, type)) return true;
  return isCatalogPending;
};

const ShopNav = () => {
  const { t } = useTranslation();
  const { isPersonalisationEnabled } = useAdonisChat();
  const { preferences } = useUserPreferences();
  const { hasEnoughListings } = useRecommendationsEligibility();
  const { isSkinDropVisible } = useSkinDropVisibility();
  const { isMyProductsVisible } = useMyProductsVisibility();
  const hasAnyVisibleProducts = isSkinDropVisible || isMyProductsVisible;

  const { data: platform } = useQuery({ ...getPlatform.keyed() });
  const { user } = useUser();

  const isGiftCardsEnabled = platform?.platformSettings?.giftCardEnabled;
  const isMembershipsEnabled = platform?.platformSettings?.membershipsEnabled ?? true;
  const isTreatmentsAndPackagesEnabled =
    platform?.platformSettings?.treatmentsAndPackagesEnabled ?? true;

  // The index's facet counts keep the common path cheap. A missing category is verified against
  // the location-scoped catalog API before deciding whether its tab has content, and linked
  // unlisted listings remain visible for the patient's unlock window.
  const {
    data: catalog,
    isFetched: isCatalogFetched,
    isPending: isCatalogPending,
  } = useCatalogSummary();
  const { data: unlockedListings } = useQuery(unlockedListingsQuery());
  const catalogTreatmentCount = catalog?.productTypeCounts[ALGOLIA_PRODUCT_TYPES.treatment];
  const catalogPackageCount = catalog?.productTypeCounts[ALGOLIA_PRODUCT_TYPES.package];
  const { data: verifiedListings } = useCatalogListingsVerification({
    catalog,
    enabled: isTreatmentsAndPackagesEnabled,
  });
  const { data: memberships, isFetched: isMembershipsFetched } = useQuery(
    membershipsWithUnlockedQuery(),
  );
  const { data: shopData, isFetched: isShopDataFetched } = useQuery({ ...getShop.keyed() });
  const { data: customShopCategories } = useQuery({
    ...getShopCustomCategoriesListings.keyed(),
    staleTime: 1000 * 10,
  });
  // Gift cards are scoped to the locations they are purchasable at. Filter the tab-visibility
  // list by the user's location (matching ShopGiftCardsPage) so the Gift Cards tab does not show
  // when no gift cards are purchasable at the user's location.
  const giftCardsLocationId = user?.platformLocation?.id;
  const { data: giftCards, isFetched: isGiftCardsFetched } = useQuery({
    ...getGiftCards.keyed(giftCardsLocationId ? { LocationId: giftCardsLocationId } : {}),
    enabled: isGiftCardsEnabled && !!user,
  });

  const { data: retailProducts, isFetched: isRetailProductsFetched } = useQuery({
    ...getRetailproductsByPlatformIdProductListings.keyed({
      platformId: platform?.id ?? "",
      // Unlisted products the patient opened by link stay browsable for the unlock window; the
      // products read filters them out unless their ids are passed back.
      unlockedListingIds: getUnlockedProductListingIds(),
    }),
    enabled: !!(hasAnyVisibleProducts && platform?.id),
  });

  const availableCustomShopCategories =
    customShopCategories?.filter((csc) => csc.categoryInclusions.length > 0) ?? [];

  const showTreatments = shouldShowListingTab({
    catalogCount: catalogTreatmentCount,
    isCatalogPending,
    verifiedListings,
    unlockedListings,
    type: "Treatment",
  });
  const showPackages = shouldShowListingTab({
    catalogCount: catalogPackageCount,
    isCatalogPending,
    verifiedListings,
    unlockedListings,
    type: "Package",
  });
  const hasConcerns = Object.keys(catalog?.concernCounts ?? {}).length > 0;
  const hasCustomShopCategoriesWithGiftCards =
    isGiftCardsEnabled &&
    availableCustomShopCategories.some((csc) =>
      csc.categoryInclusions.some(
        (ci) => ci.packageListingId && giftCards?.some((gc) => gc.id === ci.packageListingId),
      ),
    );

  const showJustForYouLink =
    isPersonalisationEnabled &&
    hasEnoughListings &&
    preferences?.concernIds &&
    preferences.concernIds.length > 0;

  return (
    <nav
      aria-label={t("shopPage.title")}
      className="mx-auto flex w-full max-w-6xl flex-row gap-6 overflow-x-auto whitespace-nowrap px-6 text-secondary desk:px-8"
    >
      <NavButton exact to="/shop">
        {t("shopPage.browse")}
      </NavButton>
      {showJustForYouLink && (
        <NavButton to="/shop/recommended-treatments">
          <div className="flex items-center gap-2">
            <AdonisAiIcon className="size-4" />
            <span>{t("shopPage.justForYou")}</span>
          </div>
        </NavButton>
      )}
      {availableCustomShopCategories.map((customShopCategory) => (
        <NavButton
          key={customShopCategory.category?.id}
          to={getCustomCategoryDestinationUrl(
            customShopCategory,
            hasCustomShopCategoriesWithGiftCards,
          )}
        >
          {`${customShopCategory.category.subTitle}`}
        </NavButton>
      ))}
      {isMembershipsEnabled &&
        (!isMembershipsFetched || (memberships && memberships.length > 0)) && (
          <NavButton to="/shop/memberships">{t("shopPage.memberships")}</NavButton>
        )}
      {isTreatmentsAndPackagesEnabled && showTreatments && (
        <NavButton to="/shop/treatments">{t("shopPage.treatments")}</NavButton>
      )}
      {hasAnyVisibleProducts &&
        (!isRetailProductsFetched || (retailProducts && retailProducts.length > 0)) && (
          <NavButton to="/shop/products">{t("shopPage.products")}</NavButton>
        )}
      {isTreatmentsAndPackagesEnabled && showPackages && (
        <NavButton to="/shop/packages">{t("shopPage.packages")}</NavButton>
      )}
      {isGiftCardsEnabled && (!isGiftCardsFetched || (giftCards && !!giftCards.length)) && (
        <NavButton to="/shop/gift-cards">{t("shopPage.giftCards")}</NavButton>
      )}
      {(!isShopDataFetched ||
        !isCatalogFetched ||
        (shopData?.concerns && shopData.concerns.length > 0 && hasConcerns)) && (
        <NavButton to="/shop/concerns">{t("shopPage.browseByConcern")}</NavButton>
      )}
    </nav>
  );
};

/**
 * The placeholder a loading /shop/* destination shows under the shop header, or null to keep the
 * current page up: a filter or sort only rewrites the page's own search, and it revalidates in
 * place. The filterable lists (treatments, packages, categories, a concern's list) share one
 * skeleton, and the few other destinations are close enough to it to use it too.
 */
export const pendingShopSkeleton = (pendingPath: string | undefined, currentPath: string) => {
  if (!pendingPath?.startsWith("/shop/") || pendingPath === currentPath) return null;
  if (matchPath(publicStorefrontPaths.shopProducts, pendingPath)) return ShopProductsSkeleton;
  if (matchPath("/shop/gift-cards", pendingPath)) return ShopGiftCardsSkeleton;
  if (matchPath(publicStorefrontPaths.shopMemberships, pendingPath)) {
    return ShopMembershipsSkeleton;
  }
  return ShopListingSkeleton;
};

const ShopPage = () => {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const location = useLocation();
  const PendingSkeleton = pendingShopSkeleton(
    navigation.state === "loading" ? navigation.location.pathname : undefined,
    location.pathname,
  );
  useEffect(() => {
    // Warm only lazy Shop route modules after Shop mounts; their data remains route-owned.
    void Promise.allSettled([
      import("pages/root/shop/ShopTreatmentsPage"),
      import("pages/root/shop/ShopPackagesPage"),
      import("pages/root/shop/ShopGiftCardsPage"),
      import("pages/root/shop/ShopProductsPage"),
    ]);
  }, []);

  const header = (
    <TopNav
      footer={<ShopNav />}
      contentClassName="desk:max-w-6xl desk:px-8 desk:py-2 desk2xl:max-w-6xl"
    >
      <h1 className="text-xl">{t("shopPage.title")}</h1>
    </TopNav>
  );

  if (PendingSkeleton)
    return (
      <PendingRouteViewport>
        {header}
        <PendingSkeleton />
      </PendingRouteViewport>
    );

  return (
    <>
      {header}
      <Outlet />
    </>
  );
};

export default ShopPage;
